import { apiClient } from "../utils/apiClient";
import { ORGANIZER_SETTLEMENTS_URL } from "../config/api";
import { PaymentProvider } from "./orderService";

// Settlement statements (ms-payments-integration, docs/settlement-statement-decision.md): SuperTicket's service fee
// per event. OPEN while the event runs (accrued so far), ISSUED at the cut-off (end + 1 day), VOID when cancelled.
export type SettlementStatus = 'OPEN' | 'ISSUED' | 'VOID';

export interface SettlementSummary {
    eventId: string;
    eventName?: string;
    status: SettlementStatus;
    currency: string;
    tickets: number;
    serviceFee: number;
    amountDue: number;
    // Paid orders still without tickets: shown, not billed
    pendingOrders: number;
    // Instants (…Z); null while the event has no end date or the statement is open
    cutOffAt?: string | null;
    issuedAt?: string | null;
}

export interface SettlementLine {
    stageId?: string;
    stageName?: string;
    provider?: PaymentProvider;
    tickets: number;
    ticketSales: number;
    // VAT included
    serviceFee: number;
}

export interface Settlement {
    eventId: string;
    organizerId: string;
    organizationName?: string | null;
    eventName?: string;
    // Event wall times, as the organizer typed them
    eventStart?: string | null;
    eventEnd?: string | null;
    status: SettlementStatus;
    currency: string;
    terms?: {
        payer: 'BUYER' | 'ORGANIZER';
        rate: number;
        minAmount: number;
        maxAmount?: number | null;
        taxMode: 'TAX_INCLUDED' | 'TAX_ADDED';
    } | null;
    lines: SettlementLine[];
    totals: {
        orders: number;
        tickets: number;
        ticketSales: number;
        serviceFee: number;
        serviceFeeNet: number;
        serviceFeeTax: number;
    };
    amountDue: number;
    pending: { orders: number; serviceFee: number };
    cutOffAt?: string | null;
    issuedAt?: string | null;
}

// attachment; filename="liquidacion-noche-de-rock.csv"
const fileNameOf = (contentDisposition: string | null, fallback: string) =>
    contentDisposition?.match(/filename="?([^";]+)"?/)?.[1] ?? fallback;

export const organizerSettlementsService = {
    async getMySettlements(): Promise<SettlementSummary[]> {
        const response = await apiClient(ORGANIZER_SETTLEMENTS_URL, { cache: 'no-store' });
        if (!response.ok) {
            throw new Error('No fue posible consultar las liquidaciones');
        }
        return await response.json();
    },

    async getSettlement(eventId: string): Promise<Settlement> {
        const response = await apiClient(`${ORGANIZER_SETTLEMENTS_URL}/${encodeURIComponent(eventId)}`, { cache: 'no-store' });
        if (!response.ok) {
            throw new Error('No fue posible consultar la liquidación');
        }
        return await response.json();
    },

    // Saves the CSV through a temporary link: the file comes with the session cookie, so a plain link would not do
    async downloadCsv(eventId: string): Promise<void> {
        const response = await apiClient(`${ORGANIZER_SETTLEMENTS_URL}/${encodeURIComponent(eventId)}/csv`, { cache: 'no-store' });
        if (!response.ok) {
            throw new Error('No fue posible descargar la liquidación');
        }
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url;
        link.download = fileNameOf(response.headers.get('Content-Disposition'), `liquidacion-${eventId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }
};
